/**
 * Shared logic for the Teammate chat surfaces (the test bench FTSTeammate.vue and
 * the in-app GNey popup FTSClientTeammate.vue). Anything that was duplicated and is
 * not view-specific lives here: the markdown engine, cost/usage accounting, history
 * building, and entry/module reference links.
 *
 * Components opt in with `mixins: [teammateChatMixin]` and override `chatModules()`
 * to point the reference resolver at their own module source.
 *
 * Markdown reference chips and tables emit shared class names (tmd-*) styled by the
 * sibling teammateChat.css, which this module pulls in so it ships wherever the
 * mixin is used.
 */
import commons from "../commons/commons";
import webAppConfig from "../commons/webApp.config";
import "./teammateChat.css";

// Local cost estimate when the provider doesn't report one. USD per 1M tokens.
// Update if the Teammate model changes; a provider-reported cost always wins.
export const PRICING = { input: 1, output: 5, label: "Claude Haiku 4.5" };

// Customer-facing usage is shown in credits rather than raw USD. The server prices
// each turn at deduction time (CostUsd × rate × margin factor — ops ×3, design ×5;
// see AITeammateService.ConsumeCreditsSafeAsync) and sends the receipt as
// usage.credits — that figure ALWAYS wins, or the panel disagrees with the wallet.
// CREDIT_PER_USD is only the unfactored fallback for turns from before the server
// priced them. (Raw USD stays on internal optimization surfaces.)
export const CREDIT_PER_USD = 100;

// Sentinel that brackets extracted code spans during markdown parsing. NUL never
// appears in model output, so placeholders can't collide with real text.
const SENTINEL = String.fromCharCode(0);

// Trailing "opens in app" glyph on clickable reference chips. Inline SVG (not a
// font-awesome component) because chips render through v-html, which can't mount
// Vue components; currentColor keeps it on the chip's type color.
const OPEN_ICON =
  '<svg class="tmd-ref-open" viewBox="0 0 12 12" aria-hidden="true">'
  + '<path d="M4.6 2H2.6A1.6 1.6 0 0 0 1 3.6v5.8A1.6 1.6 0 0 0 2.6 11h5.8A1.6 1.6 0 0 0 10 9.4V7.4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>'
  + '<path d="M6.8 1H11v4.2M11 1 5.6 6.4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>'
  + '</svg>';

export default {
  methods: {
    /**
     * The module list used to resolve {{module:entry}} references and action links.
     * Override in the component (e.g. return this.modules, or a store getter).
     */
    chatModules() {
      return [];
    },

    /* ---------------- attachments ---------------- */
    /**
     * Map attachment objects to the API payload shape. Pre-uploaded attachments (the picker
     * staged them to S3 via request-upload-url/confirm-upload) travel as a { name, path,
     * mimeType, size } ref — no base64 re-send, and the server can attach the ref to entry
     * File/Image fields. Inline base64 ({ name, mimeType, data }) remains the fallback when
     * the upload didn't happen (S3 disabled, CORS, network). Attachments with neither are
     * dropped (their chip already shows the failed state).
     */
    attachmentsPayload(list) {
      return (list || [])
        .filter((a) => a && (a.path || a.data))
        .map((a) => a.path
          ? { name: a.name, path: a.path, mimeType: a.mimeType, size: a.size }
          : { name: a.name, mimeType: a.mimeType, data: a.data });
    },
    /** Lightweight copy kept on a chat bubble for preview (avoids re-holding base64 in History). */
    attachmentsPreview(list) {
      return (list || []).map((a) => ({
        isImage: a.isImage,
        dataUrl: a.dataUrl,
        name: a.name,
        path: a.path || null,
        mimeType: a.mimeType || "",
      }));
    },
    /**
     * Resolves after every staged upload in the list settled (each attachment carries its
     * upload promise as `_upload`; it never rejects). Call before attachmentsPayload so a
     * send that races an in-flight upload waits for the path instead of falling back to
     * base64 (or silently dropping a large file).
     */
    awaitAttachmentUploads(list) {
      const pending = (list || []).map((a) => a && a._upload).filter(Boolean);
      return pending.length ? Promise.all(pending) : Promise.resolve();
    },
    /** Workspace id used to build chat-attachment download URLs. Override in the component. */
    chatWorkspaceId() {
      return "";
    },
    /**
     * Servable download URL for a stored chat attachment ref ("/s/{shortId}/{fileName}").
     * Chat uploads live at the workspace-level S3 key, so the workspace-prefixed download
     * route resolves them (same pattern as ShareForm / commons.filePath).
     */
    chatFileHref(path) {
      const ws = this.chatWorkspaceId();
      if (!path || !ws) return null;
      const idx = path.indexOf("/s/");
      if (idx < 0) return null;
      const rest = path.substring(idx + 3).split("/");
      if (rest.length < 2) return null;
      const guid = rest.shift();
      const url = `/api/d/${ws}/s/${guid}/${encodeURIComponent(rest.join("/"))}`;
      return webAppConfig.baseURL ? `${webAppConfig.baseURL}${url}` : url;
    },

    /* ---------------- usage / cost ---------------- */
    /** Normalize the response's usage block; null when there's nothing to show. */
    extractUsage(data) {
      const u = data && data.usage;
      if (!u) return null;
      const input = Number(u.inputTokens || 0);
      const output = Number(u.outputTokens || 0);
      if (!input && !output) return null;
      return {
        input,
        output,
        // Prompt-cache stats. The server normalizes all providers to the same
        // semantics: input is the uncached remainder, cacheRead/cacheWrite the rest.
        cacheRead: Number(u.cacheReadInputTokens || 0),
        cacheWrite: Number(u.cacheCreationInputTokens || 0),
        // Provider-reported USD cost (OpenRouter usage accounting); null → estimate locally.
        cost: u.cost != null ? Number(u.cost) : null,
        // Server-priced credit charge for the turn (margin factors applied at the
        // wallet). Null on turns from before the server sent it.
        credits: u.credits != null ? Number(u.credits) : null,
        toolCalls: Number(u.toolCalls || 0),
        // Wall-clock time for the whole turn (server-measured), in milliseconds.
        latencyMs: Number(u.latencyMs || 0),
        // Model that served this turn (e.g. "anthropic/claude-haiku-4.5"); "" when
        // the server didn't run a model (a pure button-click confirm carries no model).
        model: u.model ? String(u.model) : "",
      };
    },
    pricingLabel() {
      return PRICING.label;
    },
    /** Short model label for the chip: drop the provider prefix ("anthropic/x" -> "x"). */
    formatModel(model) {
      if (!model) return "";
      const slash = model.lastIndexOf("/");
      return slash >= 0 ? model.slice(slash + 1) : model;
    },
    formatNumber(n) {
      const v = Number(n);
      return isFinite(v) ? v.toLocaleString() : "0";
    },
    /** Human-friendly turn latency: ms under a second, else seconds with one decimal. */
    formatLatency(ms) {
      const v = Number(ms);
      if (!isFinite(v) || v <= 0) return "";
      return v < 1000 ? `${Math.round(v)}ms` : `${(v / 1000).toFixed(1)}s`;
    },
    /** Total prompt+completion tokens incl. the cached portion of the prompt. */
    totalTokens(usage) {
      if (!usage) return 0;
      return (usage.input || 0) + (usage.output || 0) + (usage.cacheRead || 0) + (usage.cacheWrite || 0);
    },
    estimateCost(usage) {
      if (!usage) return 0;
      const input = Number(usage.input) || 0;
      const output = Number(usage.output) || 0;
      // Anthropic cache pricing: reads ~0.1x input price, writes ~1.25x.
      const cacheRead = Number(usage.cacheRead) || 0;
      const cacheWrite = Number(usage.cacheWrite) || 0;
      const effectiveInput = input + cacheRead * 0.1 + cacheWrite * 1.25;
      return (effectiveInput * PRICING.input + output * PRICING.output) / 1e6;
    },
    /** Provider-reported cost when available, local estimate otherwise. */
    costOf(usage) {
      if (!usage) return 0;
      return usage.cost > 0 ? usage.cost : this.estimateCost(usage);
    },
    /**
     * Credits for one turn: the server's priced charge when present (the wallet
     * receipt, margin factors included), else the unfactored cost conversion for
     * pre-factor turns. Display-side pricing beyond this fallback is forbidden —
     * the shown number must equal the wallet debit.
     */
    creditsOf(usage) {
      if (!usage) return 0;
      if (usage.credits > 0) return usage.credits;
      return this.costOf(usage) * CREDIT_PER_USD;
    },
    /** True when the figure is a real charge (server-priced or provider-reported), not an estimate. */
    creditsReported(usage) {
      return !!usage && (usage.credits > 0 || usage.cost > 0);
    },
    costTitle(usage) {
      return usage && usage.cost > 0
        ? "Cost reported by the provider"
        : `Estimated cost (${PRICING.label} rates)`;
    },
    formatCost(usd) {
      const v = Number(usd);
      if (!isFinite(v) || v <= 0) return "$0.0000";
      return v >= 1 ? "$" + v.toFixed(2) : "$" + v.toFixed(4);
    },
    /**
     * Format a credit amount for the customer-facing readout (pass creditsOf(usage)
     * or a summed credits total — NOT USD). Compact: 2 dp below a credit, 1 dp below
     * 100, whole numbers above; trailing zeros trimmed. Singular "credit" for exactly 1.
     */
    formatCredits(credits) {
      const c = Number(credits) || 0;
      if (!isFinite(c) || c <= 0) return "0 credits";
      let n;
      if (c < 1) n = c.toFixed(2);
      else if (c < 100) n = c.toFixed(1);
      else n = String(Math.round(c));
      if (n.indexOf(".") > -1) n = n.replace(/\.?0+$/, "");
      return n + (n === "1" ? " credit" : " credits");
    },
    /** Tooltip for a credit figure. Only rendered on the internal detailed
     *  readout, so it carries the raw provider USD too — the reconcile figure
     *  against the provider invoice. Customers never see this tooltip, and the
     *  $-per-credit anchor is still never stated (pricing doc §6; under margin
     *  factors it would be wrong arithmetic anyway). */
    creditTitle(usage) {
      const charged = this.creditsReported(usage)
        ? "Credits charged this turn"
        : "Estimated credits (this turn predates server-side pricing)";
      const usd = usage && usage.cost > 0
        ? ` · provider cost ${this.formatCost(usage.cost)} (reported)`
        : ` · provider cost ${this.formatCost(this.estimateCost(usage))} (estimated, ${PRICING.label} rates)`;
      return charged + usd;
    },
    /** Sum per-turn usage across a history array into session totals. */
    computeTotals(history) {
      let input = 0, output = 0, cacheRead = 0, cacheWrite = 0, toolCalls = 0, turns = 0, cost = 0, credits = 0, reported = true, latencyMs = 0;
      for (const m of (history || [])) {
        if (!m.usage) continue;
        input += m.usage.input || 0;
        output += m.usage.output || 0;
        cacheRead += m.usage.cacheRead || 0;
        cacheWrite += m.usage.cacheWrite || 0;
        toolCalls += m.usage.toolCalls || 0;
        latencyMs += m.usage.latencyMs || 0;
        // Per-turn: provider-reported cost when present, local estimate otherwise.
        cost += this.costOf(m.usage);
        // Credits sum PER TURN (design and ops turns are priced at different
        // factors — one conversion over summed dollars would be wrong).
        credits += this.creditsOf(m.usage);
        // The total is a real charge only if every turn in it was.
        if (!this.creditsReported(m.usage)) reported = false;
        turns += 1;
      }
      if (!turns) reported = false;
      return { input, output, cacheRead, cacheWrite, toolCalls, turns, cost, credits, reported, latencyMs, total: input + output + cacheRead + cacheWrite };
    },

    /* ---------------- history (for replay back to the model) ---------------- */
    /**
     * Build the History array from a chat history list. A committed-action turn
     * carries no prose (response:null), so we synthesize a summary — otherwise the
     * model can't see that a proposal was applied. Genuinely empty turns are dropped,
     * and consecutive same-role turns are merged (most APIs reject two in a row).
     */
    buildHistory(history) {
      const turns = [];
      for (const m of (history || [])) {
        if (m.error) continue;
        const content = this.historyContent(m);
        // Stored refs ({ name, path, ... }) for files attached on this user turn — echoed by the
        // server's response and round-tripped here so the model keeps the { name, path } it needs
        // to place those files into File/Image fields on LATER turns. Without this the path dies
        // with the turn it was sent in ("I don't have the file path").
        const refs = (m.role === "user" && Array.isArray(m.attachmentRefs))
          ? m.attachmentRefs.filter((r) => r && r.path) : [];
        if ((!content || !content.trim()) && !refs.length) continue;
        const last = turns[turns.length - 1];
        if (last && last.role === m.role) {
          if (content && content.trim()) last.content += "\n\n" + content;
          if (refs.length) last.attachments = (last.attachments || []).concat(refs);
        } else {
          const turn = { role: m.role, content: content || "" };
          if (refs.length) turn.attachments = refs;
          turns.push(turn);
        }
      }
      return turns;
    },
    historyContent(m) {
      if (m.content && m.content.trim()) return m.content;
      if (m.actions && m.actions.length) return this.summarizeActions(m.actions);
      return "";
    },
    /** Plain-text recap of committed actions, for replay back to the model. */
    summarizeActions(actions) {
      const lines = [];
      for (const a of actions) {
        if (!a) continue;
        if (a.error) {
          lines.push(`(action failed: ${a.message || a.error})`);
        } else if (a.type === "module") {
          const verb = a.kind === "modify" ? "Applied changes to" : "Created";
          lines.push(`${verb} module "${a.module}"${a.moduleId != null ? ` (id ${a.moduleId})` : ""}.`);
        } else {
          const parts = [`Submitted activity "${a.activity || "activity"}"`];
          if (a.module) parts.push(`on "${a.module}"`);
          if (a.entryId != null) parts.push(`entry ${a.entryId}`);
          lines.push(parts.join(" ") + ".");
        }
      }
      return lines.join("\n");
    },
    /** True when a response carries something worth dropping into the chat. */
    chatResponseHasContent(msg) {
      return !!(msg.content
        || msg.moduleChip
        || (msg.actions && msg.actions.length)
        || (msg.proposals && msg.proposals.length)
        || (msg.pendingActions && msg.pendingActions.length)
        || (msg.formPills && msg.formPills.length)
        || (msg.questions && msg.questions.length)
        || (msg.formCards && msg.formCards.length)
        || (msg.reports && msg.reports.length));
    },

    /* ---------------- references & action links ---------------- */
    lookupModule(moduleId) {
      if (!moduleId) return null;
      const target = String(moduleId);
      const mods = this.chatModules() || [];
      // Exact match on numeric id (action cards) or display name (the only
      // module identifier the model is ever handed, via list_modules).
      const exact = mods.find(
        (x) => String(x.id) === target || String(x.name) === target
      );
      if (exact) return exact;
      // Fallback: the model sometimes slugifies the name instead of copying it
      // verbatim ("JobLog", "job_log"). Match case/space/punctuation-insensitively,
      // but only when exactly one module matches so an ambiguous token never
      // resolves to the wrong module.
      const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, "");
      const key = norm(target);
      if (!key) return null;
      const fuzzy = mods.filter((x) => norm(x.name) === key);
      return fuzzy.length === 1 ? fuzzy[0] : null;
    },
    buildModuleHref(moduleId, entryId) {
      if (!moduleId) return null;
      try {
        const route = { name: "list-view", params: { moduleId: commons.encodeids(moduleId) } };
        // queryEncodeids is what the list view decodes (commons.queryDecodeids).
        if (entryId) route.query = { id: commons.queryEncodeids(moduleId, entryId) };
        const resolved = this.$router && this.$router.resolve(route);
        return resolved && resolved.href ? resolved.href : null;
      } catch (e) {
        return null;
      }
    },
    /** Heading for a committed submission (handles both module and activity results). */
    actionLabel(act) {
      if (act && act.type === "module") return act.kind === "modify" ? "Modified module" : "Created module";
      if (act && act.type === "access") return "Access change";
      const a = (act && act.activity) || "activity";
      return a.charAt(0).toUpperCase() + a.slice(1);
    },
    /** Link to the created/affected entry (or module) for an action card. */
    actionHref(act) {
      if (!act) return null;
      if (act.type === "module") return act.moduleId != null ? this.buildModuleHref(act.moduleId) : null;
      const mod = this.lookupModule(act.module);
      return mod ? this.buildModuleHref(mod.id, act.entryId) : null;
    },
    /** Display label for an open-form pill (a human / no-actor activity the user completes). */
    formPillLabel(pa) {
      const name = (pa && (pa.activity || pa.activityId)) || "activity";
      const label = String(name);
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    /**
     * Open the activity form for a pill directly, in place: the quick-view overlay resolves the
     * form from (moduleId, activityId, entryId) — activityId is the standard token (create/edit/…)
     * or a custom activity's id, entryId is null for create. When the pill carries `values` (the
     * model's proposed input, already mapped server-side to formData shape), they ride along as
     * `prefill` so the form opens populated — the user still reviews and submits (the AI never
     * commits these).
     */
    openFormPill(pa) {
      if (!pa) return;
      // When docked fullscreen (embedded teammate), drop to normal so the form isn't hidden
      // behind the overlay — mirrors how an in-place entry reference opens. No-op elsewhere.
      if (this.windowState === "full") this.windowState = "normal";
      const mod = this.lookupModule(pa.module);
      const vectorId = mod ? mod.id : pa.moduleId;
      const evt = {
        id: pa.entryId || undefined,
        vectorId: vectorId || undefined,
        activityId: pa.activityId || pa.activity || "create",
        prefill: (pa.values && Object.keys(pa.values).length) ? pa.values : undefined,
      };
      // Hook for a host to decorate the event (e.g. the internal-alpha mock attaches a mockForm so
      // the quick-view renders a fillable form for a fake module without a backend). Generic by
      // default — only FTSClientTeammate implements it, gated to mock mode.
      if (typeof this.decorateQuickViewData === "function") this.decorateQuickViewData(evt, pa, mod);
      window.eventBus.$emit("quickViewData", evt);
    },
    /**
     * Render a reference token as a clickable in-app chip. Four shapes:
     *   {{module}}                              → module chip (opens the list view)
     *   {{module:listing:listingId:label}}      → listing chip (opens that SPECIFIC saved listing;
     *                                             the literal word "listing" marks the form)
     *   {{module:entryId:name}}                 → entry chip (opens the entry's read-only quick-view)
     *   {{module:entryId:activityId:label}}     → activity chip (opens that activity's form for the
     *                                             entry in place — edit, or a custom activity)
     * `third` is the entry name in the 3-part form, or the activityId/listingId in the 4-part
     * forms; `label` is only present in the 4-part forms (the human label for the chip).
     */
    renderRef(moduleId, entryId, third, label) {
      const mod = this.lookupModule(moduleId);
      const moduleName = mod ? mod.name : null;
      const href = mod ? this.buildModuleHref(mod.id, entryId) : null;
      // The module's own emoji is the chip icon when it resolves (design) — a module reads
      // by its identity mark, not a generic glyph. Fallbacks below per chip type.
      const modEmoji = mod && mod.emoji ? String(mod.emoji) : "";

      if (!entryId) {
        const labelText = moduleName || moduleId;
        const tip = `moduleId: ${moduleId}`;
        // The name rides in .tmd-ref-label so only it carries the link underline (design) —
        // the icon and open glyph stay bare.
        const inner = `<span class="tmd-ref-icon" aria-hidden="true">${modEmoji || "📦"}</span><span class="tmd-ref-label">${labelText}</span>`;
        return href
          ? `<a class="tmd-ref tmd-ref--module" href="${href}" title="${tip}">${inner}${OPEN_ICON}</a>`
          : `<span class="tmd-ref tmd-ref--module" title="${tip}">${inner}</span>`;
      }

      // Listing form: the literal word "listing" in the entry slot marks a chip for a specific
      // saved listing (entry ids are numeric, so the word can't collide). Rendered per the design
      // as "«module» | «listing»". When the first segment resolves to a real module, the chip is
      // clickable — the host's click handler (tmd-ref--list) opens that listing, pairing it with the
      // turn's applied view when one was recorded so the teammate's exact filter/sort/search comes
      // along. The teammate also emits a module-agnostic placeholder ({{module:listing:<id>:<name>}}),
      // which doesn't resolve a module — that renders as a plain, non-navigating listing pill.
      if (third && String(entryId).toLowerCase() === "listing") {
        const listingId = third;
        const listLabel = (label && label.trim()) || listingId;
        // "«module» | «listing»" keeps the module part even when it doesn't resolve to a
        // workspace module (the raw token still names it); only the module-agnostic
        // placeholder — the literal word "module" — carries no identity and is dropped.
        const modName = moduleName
          || (String(moduleId).toLowerCase() === "module" ? "" : moduleId);
        const modPart = modName ? `${modName}<span class="tmd-ref-sep">|</span>` : "";
        const inner = `<span class="tmd-ref-icon" aria-hidden="true">${modEmoji || "🗂️"}</span><span class="tmd-ref-label">${modPart}${listLabel}</span>`;
        if (mod) {
          const tip = `listing: ${listingId} · moduleId: ${moduleId}`;
          return `<a class="tmd-ref tmd-ref--list" href="${this.buildModuleHref(mod.id)}" title="${tip}" data-vector-id="${mod.id}" data-module="${moduleName}" data-listing-id="${listingId}">${inner}${OPEN_ICON}</a>`;
        }
        return `<span class="tmd-ref tmd-ref--list" title="listing: ${listingId}">${inner}</span>`;
      }

      // Four-part form: a chip that opens the activity form for this entry in place rather than
      // just viewing it. data-activity-id is what the host's click handler keys on to open the
      // form overlay (quickViewData with activityId) instead of the read-only entry view.
      // A present `label` (4th segment) is what marks this as an activity reference: the token
      // grammar forbids ':' in the first three segments and the model is told never to put ':'
      // inside an entry name, so the extra segment reliably means "activity", never a colon that
      // belonged to the entry name. The 3rd segment is a standard token ("edit") OR a custom
      // activity NAME, which routinely contains spaces ("Submit Leave Request", "Pick Up") — the
      // model only ever knows activities by name, so accept any non-blank token and pass it
      // through; the backend resolves a name to its id (parity with the MCP get_form path).
      const isActivityRef = (s) => typeof s === "string" && /\S/.test(s);
      if (label && isActivityRef(third)) {
        const activityId = third;
        const actLabel = label || activityId;
        // Design: "EDIT ✎ JB 10082" — the verb leads as its own uppercase tag, so strip it off
        // the label when the model already led with it ("Edit JB 10082" would otherwise render
        // "EDIT Edit JB 10082"). An emptied label falls back to the entry id.
        const verbRe = new RegExp(`^${String(activityId).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b[\\s:—–-]*`, "i");
        let rest = String(actLabel).replace(verbRe, "").trim();
        if (!rest) rest = `#${entryId}`;
        const tip = `activity: ${activityId} · entryId: ${entryId} · moduleId: ${moduleId}`;
        // Module emoji as the identity mark (design) — same as the cards; 📝 only when unresolved.
        const inner = `<span class="tmd-ref-verb">${String(activityId).toUpperCase()}</span><span class="tmd-ref-icon" aria-hidden="true">${modEmoji || "📝"}</span>${rest}`;
        return href
          ? `<a class="tmd-ref tmd-ref--activity" href="${href}" title="${tip}" data-entry-id="${entryId}" data-vector-id="${mod.id}" data-activity-id="${activityId}">${inner}${OPEN_ICON}</a>`
          : `<span class="tmd-ref tmd-ref--activity" title="${tip}">${inner}</span>`;
      }

      // Rejoin a non-id-like 4th segment: the ':' was part of the entry name, not an activity split.
      const name = label ? `${third}:${label}` : third;
      const entryLabel = (name && name.trim()) || entryId;
      const tip = `entryId: ${entryId} · moduleId: ${moduleId}`;
      // Module emoji as the identity mark (design) — same as the cards; 📄 only when unresolved.
      const inner = `<span class="tmd-ref-icon" aria-hidden="true">${modEmoji || "📄"}</span>${entryLabel}`;
      // Expose the ids so a host can open the entry's quick-view in place (no navigation)
      // rather than following the href into the list view.
      return href
        ? `<a class="tmd-ref tmd-ref--entry" href="${href}" title="${tip}" data-entry-id="${entryId}" data-vector-id="${mod.id}">${inner}${OPEN_ICON}</a>`
        : `<span class="tmd-ref tmd-ref--entry" title="${tip}">${inner}</span>`;
    },
    /**
     * Clickable module chip for a committed design/modify proposal. Rendered on the commit-response
     * message (as `msg.moduleChip`) once the module is applied — it replaces the separate "Created
     * module" action card so the same result isn't shown twice. Reads the committed module that
     * confirm stashes on the proposal as `_committedModule`; returns "" until then.
     *
     * The href is built from the committed module's own `moduleId` (which the backend — and the mock —
     * return), so the pill is clickable immediately rather than only after the workspace reload
     * resolves the name. Falls back to a name lookup when no id is present, and to a plain (non-link)
     * chip when neither yields a route.
     */
    proposalModuleChip(p) {
      const cm = p && p._committedModule;
      if (!cm) return "";
      const name = cm.name != null && cm.name !== "" ? String(cm.name)
        : (cm.moduleId != null ? String(cm.moduleId) : "");
      if (!name) return "";
      const mod = cm.moduleId != null ? null : this.lookupModule(name);
      const moduleId = cm.moduleId != null ? cm.moduleId : (mod ? mod.id : null);
      const href = moduleId != null ? this.buildModuleHref(moduleId) : null;
      const tip = moduleId != null ? `moduleId: ${moduleId}` : name;
      const inner = `<span class="tmd-ref-icon" aria-hidden="true">${cm.emoji || "📦"}</span><span class="tmd-ref-label">${name}</span>`;
      return href
        ? `<a class="tmd-ref tmd-ref--module" href="${href}" title="${tip}">${inner}${OPEN_ICON}</a>`
        : `<span class="tmd-ref tmd-ref--module" title="${tip}">${inner}</span>`;
    },

    /* ---------------- markdown ----------------
     * Blocks: headings, ordered/unordered lists, blockquotes, GFM tables, fenced +
     * inline code. Inline: bold/italic, links (external → new tab, in-app #/… → SPA),
     * and Inistate references ({{moduleId:entryId:name}} / {{moduleId:entryId:activityId:label}}).
     */
    renderMarkdown(text) {
      if (!text) return "";
      let s = String(text)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

      // Pull fenced + inline code out first so their contents are never parsed.
      const fences = [];
      s = s.replace(/```([a-zA-Z0-9_-]*)\n?([\s\S]*?)```/g, (_, lang, code) => {
        const idx = fences.length; fences.push(code.replace(/\n$/, "")); return `${SENTINEL}F${idx}${SENTINEL}`;
      });
      // Unwrap a reference token the model wrapped in backticks (the prompt shows the
      // {{...}} syntax code-formatted, and weaker models copy that formatting): a code
      // span would shield the token from the chip replacement in mdInline and it would
      // read as literal text. Only a span that is exactly one token unwraps — real code
      // stays code. Runs after fence extraction so fenced examples are never touched.
      s = s.replace(/`\s*(\{\{[^{}`\n]+\}\})\s*`/g, "$1");
      const codes = [];
      s = s.replace(/`([^`\n]+)`/g, (_, c) => { const idx = codes.length; codes.push(c); return `${SENTINEL}C${idx}${SENTINEL}`; });

      const lines = s.split("\n");
      const out = [];
      let i = 0;
      const isOl = (l) => /^\s*\d+\.\s+/.test(l);
      const isUl = (l) => /^\s*[-*+]\s+/.test(l);
      const isTableSep = (l) => !!l && /\|/.test(l) && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(l);
      const splitRow = (l) => {
        let r = l.trim();
        if (r.startsWith("|")) r = r.slice(1);
        if (r.endsWith("|")) r = r.slice(0, -1);
        return r.split("|").map((c) => c.trim());
      };
      const parseAlign = (sep) => splitRow(sep).map((c) => {
        const left = c.startsWith(":"); const right = c.endsWith(":");
        if (left && right) return "center";
        if (right) return "right";
        if (left) return "left";
        return null;
      });
      const isTable = (idx) => idx + 1 < lines.length
        && /\|/.test(lines[idx]) && !isTableSep(lines[idx]) && isTableSep(lines[idx + 1]);
      const isBlock = (l, idx) => /^(#{1,6}\s|>\s?)/.test(l) || isOl(l) || isUl(l) || (idx != null && isTable(idx));

      while (i < lines.length) {
        const line = lines[i];

        if (isTable(i)) {
          const header = splitRow(line);
          const aligns = parseAlign(lines[i + 1]);
          i += 2;
          const rows = [];
          while (i < lines.length && /\|/.test(lines[i]) && !/^\s*$/.test(lines[i])) { rows.push(splitRow(lines[i])); i++; }
          const alignAttr = (idx) => aligns[idx] ? ` style="text-align:${aligns[idx]}"` : "";
          let table = '<table class="tmd-table"><thead><tr>';
          header.forEach((cell, idx) => { table += `<th${alignAttr(idx)}>${this.mdInline(cell)}</th>`; });
          table += "</tr></thead><tbody>";
          rows.forEach((r) => {
            table += "<tr>";
            const cols = Math.max(r.length, header.length);
            for (let c = 0; c < cols; c++) table += `<td${alignAttr(c)}>${this.mdInline(r[c] || "")}</td>`;
            table += "</tr>";
          });
          table += "</tbody></table>";
          out.push(table);
          continue;
        }

        const h = line.match(/^(#{1,6})\s+(.*)$/);
        if (h) { const lvl = Math.min(6, h[1].length); out.push(`<h${lvl}>${this.mdInline(h[2])}</h${lvl}>`); i++; continue; }
        if (isOl(line)) {
          const items = [];
          while (i < lines.length && isOl(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
          out.push("<ol>" + items.map((x) => `<li>${this.mdInline(x)}</li>`).join("") + "</ol>"); continue;
        }
        if (isUl(line)) {
          const items = [];
          while (i < lines.length && isUl(lines[i])) { items.push(lines[i].replace(/^\s*[-*+]\s+/, "")); i++; }
          out.push("<ul>" + items.map((x) => `<li>${this.mdInline(x)}</li>`).join("") + "</ul>"); continue;
        }
        if (/^>\s?/.test(line)) {
          const quoted = [];
          while (i < lines.length && /^>\s?/.test(lines[i])) { quoted.push(lines[i].replace(/^>\s?/, "")); i++; }
          out.push("<blockquote>" + this.mdInline(quoted.join("<br>")) + "</blockquote>"); continue;
        }
        if (/^\s*$/.test(line)) { i++; continue; }
        const para = [];
        while (i < lines.length && !/^\s*$/.test(lines[i]) && !isBlock(lines[i], i)) { para.push(lines[i]); i++; }
        out.push("<p>" + this.mdInline(para.join("<br>")) + "</p>");
      }
      let html = out.join("");
      html = html.replace(new RegExp(`${SENTINEL}C(\\d+)${SENTINEL}`, "g"), (_, idx) => `<code>${codes[+idx]}</code>`);
      html = html.replace(new RegExp(`${SENTINEL}F(\\d+)${SENTINEL}`, "g"), (_, idx) => `<pre><code>${fences[+idx]}</code></pre>`);
      return html;
    },
    mdInline(text) {
      if (!text) return "";
      let t = text;
      t = t.replace(/\*\*([^\n*]+?)\*\*/g, "<strong>$1</strong>");
      t = t.replace(/__([^\n_]+?)__/g, "<strong>$1</strong>");
      t = t.replace(/\*([^\n*]+?)\*/g, "<em>$1</em>");
      t = t.replace(/(^|[^\w])_([^\n_]+?)_(?=[^\w]|$)/g, "$1<em>$2</em>");
      // Standard markdown links: external → new tab; in-app (#/… hash routes) → SPA nav.
      t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, url) => {
        if (/["'<>]/.test(url)) return m;
        if (/^(https?:\/\/|mailto:)/i.test(url)) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
        }
        if (/^[#/]/.test(url)) return `<a href="${url}">${label}</a>`;
        return m;
      });
      // Inistate references: {{moduleId}}, {{moduleId:entryId}}, {{moduleId:entryId:name}},
      // {{moduleId:entryId:activityId:label}}. The first three segments forbid ':' so the optional
      // fourth (the activity label) is what disambiguates an activity chip from an entry chip; the
      // label segment alone may contain ':'.
      t = t.replace(
        /\{\{([^|:}\n]+?)(?::([^|:}\n]+?)(?::([^|:}\n]+?)(?::([^}\n]+?))?)?)?\}\}/g,
        (_, moduleId, entryId, third, label) => this.renderRef(
          (moduleId || "").trim(), (entryId || "").trim(),
          (third || "").trim(), (label || "").trim()
        )
      );
      return t;
    },
    prettyJson(value) {
      try { return JSON.stringify(value, null, 2); } catch (e) { return String(value); }
    },
  },
};
